import { envConfig } from "@/config";
import { useUserSessionStore } from "@/stores/v2/userSessionStore";
import { useQuery, UseQueryResult } from "@tanstack/react-query";

export interface AcademyDeviceConfig {
	classwize_enabled: boolean;
}

// Helper function to get the academy API base URL based on region
const getAcademyApiBaseUrl = (region: string): string => {
	// Check if we're in development and have an override
	if (process.env.NODE_ENV === "development" && envConfig.ACADEMY_API_URL_OVERRIDE) {
		return envConfig.ACADEMY_API_URL_OVERRIDE;
	}

	// For development, use relative URLs that will go through the proxy
	if (process.env.NODE_ENV === "development") {
		return "/academy";
	}

	// use region-specific URLs
	switch (region) {
		case "syd-2":
			return envConfig.ACADEMY_API_URL_AU || "";
		case "us-1":
			return envConfig.ACADEMY_API_URL_US || "";
		case "uk-1":
			return envConfig.ACADEMY_API_URL_UK || "";
		case "sit":
			return envConfig.ACADEMY_API_URL_SIT || "";
		default:
			// Default to US for other regions or when region is not available
			return envConfig.ACADEMY_API_URL_US || "";
	}
};

export class ResponseError extends Error {
	status?: number;
	constructor(message: string, status?: number) {
		super(message);
		this.status = status;
	}
}

async function getAcademyApplianceConfig(): Promise<AcademyDeviceConfig> {
	const { getToken, getActiveDevice, deviceinfo } = useUserSessionStore.getState();
	const deviceId = getActiveDevice();
	const token = getToken();

	if (!deviceId || !token || !deviceinfo?.region) {
		throw new Error("Device information not available in session.");
	}

	const baseUrl = getAcademyApiBaseUrl(deviceinfo.region);
	const url = `${baseUrl}/api/v1/device/${deviceId}/config`;

	const response = await fetch(url, {
		method: "GET",
		headers: {
			"Content-Type": "application/json",
			Device: deviceId,
			Token: token,
		},
	});

	if (!response.ok) {
		throw new ResponseError(`Request failed with status ${response.status}`, response.status);
	}

	const data = (await response.json()) as { config: AcademyDeviceConfig };
	const config = data.config;

	return config;
}

/**
 * Query to get the enabled feature flags for a given appliance ID.
 *
 * @example
 * ```ts
 * const academyFeatureFlagsQuery = useAcademyFeatureFlagsQuery("example-appliance-id");
 * // Check if the feature flag is enabled
 * academyFeatureFlagsQuery.data.includes("FEATURE_FLAG_NAME");
 * ```
 * @returns A query result with the enabled feature flags.
 */
export function useAcademyFeatureFlagsQuery(): UseQueryResult<AcademyDeviceConfig> {
	return useQuery({
		queryKey: ["academyFeatureFlags"],
		queryFn: async () => {
			return await getAcademyApplianceConfig();
		},
		// feature flags rarely change and changes aren't needed immediately - only need to fetch them once per session (refresh site to re-fetch)
		// In order to use staleTime set to Infinity, we cannot provide an initialData, or it will never be fetched.
		cacheTime: Infinity,
		staleTime: Infinity,
	});
}
